import { TypographyH2 } from "@/components/ui/typography"
import { useUserChangePassForm } from "./model"
import { Controller } from "react-hook-form"
import { Label } from "@/components/ui/label"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupError,
  InputGroupInput,
} from "@/components/ui/input-group"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { AlertCircleIcon, EyeIcon, EyeOffIcon, Lock } from "lucide-react"
import { Alert, AlertTitle } from "@/components/ui/alert"
import { useAutoTranslation } from "@/entities"
import { DISCOVERED_TRANSLATION_KEYS } from "@/shared/constants"

export const UserChangePassForm = () => {
  const { t } = useAutoTranslation()
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const {
    control,
    errors,
    formError,
    handleSubmit,
    isLoading,
    passConfErr,
    setFormError,
  } = useUserChangePassForm()

  return (
    <form onSubmit={handleSubmit}>
      <TypographyH2>
        🔒 {t(DISCOVERED_TRANSLATION_KEYS.CHANGE_PASSWORD)}
      </TypographyH2>

      <div className="my-6 flex flex-col gap-6">
        <Controller
          control={control}
          name="oldPassword"
          rules={{
            required: {
              value: true,
              message: t(DISCOVERED_TRANSLATION_KEYS.OLD_PASSWORD_IS_REQUIRED),
            },
            minLength: {
              value: 6,
              message: t(
                DISCOVERED_TRANSLATION_KEYS.PASSWORD_MUST_BE_AT_LEAST6_CHARACTERS_LONG
              ),
            },
          }}
          render={({ field }) => (
            <div className="flex flex-col gap-2">
              <Label htmlFor="old-password">
                {t(DISCOVERED_TRANSLATION_KEYS.OLD_PASSWORD)}
              </Label>
              <InputGroup>
                <InputGroupInput
                  type={showOldPassword ? "text" : "password"}
                  id="old-password"
                  value={field.value}
                  onChange={field.onChange}
                  aria-invalid={!!errors.oldPassword?.message}
                />
                <InputGroupAddon align="inline-end">
                  <Button
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    variant="ghost"
                    size="icon"
                    className="hover:bg-transparent"
                  >
                    {showOldPassword ? <EyeIcon /> : <EyeOffIcon />}
                  </Button>
                </InputGroupAddon>
                <InputGroupAddon>
                  <Lock />
                </InputGroupAddon>
                <InputGroupError>{errors.oldPassword?.message}</InputGroupError>
              </InputGroup>
            </div>
          )}
        />

        <Controller
          control={control}
          name="newPassword"
          rules={{
            required: {
              value: true,
              message: t(DISCOVERED_TRANSLATION_KEYS.NEW_PASSWORD_IS_REQUIRED),
            },
            minLength: {
              value: 6,
              message: t(
                DISCOVERED_TRANSLATION_KEYS.PASSWORD_MUST_BE_AT_LEAST6_CHARACTERS_LONG
              ),
            },
          }}
          render={({ field }) => (
            <div className="flex flex-col gap-2">
              <Label htmlFor="new-password">
                {t(DISCOVERED_TRANSLATION_KEYS.NEW_PASSWORD)}
              </Label>
              <InputGroup>
                <InputGroupInput
                  type={showPassword ? "text" : "password"}
                  id="new-password"
                  value={field.value}
                  onChange={field.onChange}
                  aria-invalid={!!errors.newPassword?.message}
                />
                <InputGroupAddon align="inline-end">
                  <Button
                    onClick={() => setShowPassword(!showPassword)}
                    variant="ghost"
                    size="icon"
                    className="hover:bg-transparent"
                  >
                    {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                  </Button>
                </InputGroupAddon>
                <InputGroupAddon>
                  <Lock />
                </InputGroupAddon>
                <InputGroupError>{errors.newPassword?.message}</InputGroupError>
              </InputGroup>
            </div>
          )}
        />

        <Controller
          control={control}
          name="confirmNewPassword"
          rules={{
            required: {
              value: true,
              message: t(
                DISCOVERED_TRANSLATION_KEYS.CONFIRM_NEW_PASSWORD_IS_REQURIED
              ),
            },
            minLength: {
              value: 6,
              message: t(
                DISCOVERED_TRANSLATION_KEYS.PASSWORD_MUST_BE_AT_LEAST6_CHARACTERS_LONG
              ),
            },
          }}
          render={({ field }) => (
            <div className="flex flex-col gap-2">
              <Label htmlFor="confirm-new-password">
                {t(DISCOVERED_TRANSLATION_KEYS.CONFIRM_NEW_PASSWORD)}
              </Label>
              <InputGroup>
                <InputGroupInput
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirm-new-password"
                  value={field.value}
                  onChange={field.onChange}
                  aria-invalid={!!passConfErr}
                />
                <InputGroupAddon align="inline-end">
                  <Button
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    variant="ghost"
                    size="icon"
                    className="hover:bg-transparent"
                  >
                    {showConfirmPassword ? <EyeIcon /> : <EyeOffIcon />}
                  </Button>
                </InputGroupAddon>
                <InputGroupAddon>
                  <Lock />
                </InputGroupAddon>
                <InputGroupError>
                  {passConfErr || errors.confirmNewPassword?.message}
                </InputGroupError>
              </InputGroup>
            </div>
          )}
        />
      </div>

      {formError && (
        <Alert variant="destructive" onClose={() => setFormError("")}>
          <AlertCircleIcon className="size-4" />
          <AlertTitle>{formError}</AlertTitle>
        </Alert>
      )}

      <Button type="submit" isLoading={isLoading}>
        {t(DISCOVERED_TRANSLATION_KEYS.CHANGE_PASSWORD)}
      </Button>
    </form>
  )
}
